import { Outlet } from 'react-router-dom'
import { ErrorBoundary } from '@/components/error-boundary'
import { Sidebar } from '@/components/sidebar'
import { TopHeader } from '@/components/top-header'
import { PinLock } from '@/components/pin-lock'
import { LiveSync } from '@/components/live-sync'
import { TillReceiverMount } from '@/components/tables/till-receiver-mount'
import { RequestReceiverMount } from '@/components/tables/request-receiver-mount'
import { OrderAlerts } from '@/components/order-alerts'
import { SupportAlerts } from '@/components/support-alerts'
import { StoreHydrator } from '@/components/store-hydrator'
import { BillingBanner } from '@/components/billing/billing-banner'
import { FirstDayBanner } from '@/components/first-day-banner'
import { SupportBanner } from '@/components/support/support-banner'
import { OfflineBanner } from '@/components/offline-banner'
import { AnnouncementBanner } from '@/components/announcement-banner'
import { ReadOnlyBanner } from '@/components/read-only-banner'
import { KioskGuard } from '@/components/kiosk-guard'
import { KitchenGuard } from '@/components/kitchen-guard'
import { MobileTabBar } from '@/components/mobile-tab-bar'
import { AiChat } from '@/components/ai/ai-chat'
import { TouchKeyboard } from '@/components/touch-keyboard'
import { ScreenFitDialog } from '@/components/screen-fit-dialog'
import { ExpiryWindow } from '@/components/expiry-window'

/**
 * The shop shell — `app/(app)/layout.tsx`, with `<Outlet />` where `children`
 * used to be. Nothing else about it changes: every component below is imported
 * from `components/` unmodified, which is the point of the migration plan.
 *
 * ⚠️ This is the cold path of every logged-in page. No heavy library may be
 * statically imported here or in anything it pulls in — the standing rule from
 * the Error 1102 work (framer-motion, xlsx, recharts, jsbarcode are all out).
 * The reason has changed (CDN-served bundle rather than Worker CPU) but the
 * consequence has not: whatever lands here is downloaded before the first sale
 * of the morning, on shop hardware and a shop connection.
 */
export function AppLayout() {
  return (
    <StoreHydrator>
      <KioskGuard />
      <KitchenGuard />
      <PinLock />
      {/* „მოარგე ეკრანი": once, on a new computer or tablet, past the PIN
          (components/screen-fit-dialog.tsx; light — lucide + i18n) */}
      <ScreenFitDialog />
      {/* 1.61 — the owner's once-a-day word on expiring batches */}
      <ExpiryWindow />
      {/* 106: another device's sale, purchase or price reaches this screen */}
      <LiveSync />
      {/* 1.68 — a till that is also the bar, or prints the waiters' checks (light here; the receiver loads on its own) */}
      <TillReceiverMount />
      {/* 1.69 — the guests' QR requests chime on any page (light here; the receiver loads on its own) */}
      <RequestReceiverMount />
      {/* a new online order chimes, says so and lights the menu — wherever the till is */}
      <OrderAlerts />
      {/* 1.45 — the platform admin only: a shop's help request chimes and counts on „პლატფორმა" */}
      <SupportAlerts />
      {/* Phones (2026-09-11): the page IS the app surface — edge to edge, no
          card inside the screen, content scrolling UNDER the floating tab
          capsule. `app-shell-surface` strips the card look below md
          (globals.css → MOBILE APP SHELL); the bottom padding is the capsule's
          clearance, read from one variable. md+: the floating-card dashboard,
          exactly as before.
          `overscroll-y-contain`: reaching the top of the list must not hand
          the gesture to Chrome's pull-to-refresh and reload the till. */}
      <div className="flex h-dvh overflow-hidden pt-[env(safe-area-inset-top)] md:p-4 gap-0 md:gap-4 relative z-0">
        <Sidebar />
        <div className="flex-1 min-w-0 flex flex-col overflow-hidden gap-1 md:gap-4">
          <TopHeader />
          <div className="empty:hidden flex flex-col gap-2 px-3 md:px-0 md:gap-4">
            <OfflineBanner />
            <ReadOnlyBanner />
            <AnnouncementBanner />
            <SupportBanner />
            <BillingBanner />
            <FirstDayBanner />
          </div>
          <main className="app-shell-surface flex-1 overflow-y-auto overscroll-y-contain floating-panel rounded-3xl px-4 pt-2 pb-[calc(var(--tab-capsule-top)+1rem)] sm:px-5 md:p-5 lg:p-6">
            {/* A page that throws must not take the sidebar and the tab bar
                with it — from a blank screen there is nowhere to press. */}
            <ErrorBoundary label="page">
              <Outlet />
            </ErrorBoundary>
          </main>
        </div>
      </div>
      <MobileTabBar />
      {/* 🤖 the AI manager's chat — the owner of a switched-on shop only; light
          (lucide + i18n), nothing heavy on this cold path */}
      <AiChat />
      {/* the on-screen keyboard — only on a device that switched it on (a
          touch-screen till); light (lucide + i18n), nothing heavy here */}
      <TouchKeyboard />
    </StoreHydrator>
  )
}
